import { Text } from '@eo-locale/react';
import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { ErrorResponse, HttpClient, UserApi } from '../../api';
import { RegisterFormFields, useApiErrorHandler, useModal } from '../../hooks';
import { Button } from '../atoms';
import { NewsletterModal } from '../molecules';
import styles from './EditPersonalDataForm.module.css';
import { UserForm } from './UserForm';

import { useCardTypes } from '../../hooks/useCardTypes';
import { CardTypeModal, CardTypeModalRef } from '../molecules/CardTypeModal';
import { CreditCard } from 'lucide-react';

interface Props {
  onSuccess(): void;
}

export const EditPersonalDataForm: FC<Props> = ({ onSuccess }) => {
  const queryClient = useQueryClient();
  const newsletterModal = useModal();
  const { data: user } = useQuery('user', UserApi.fetch);

  const { data: cardTypeOptions = [] } = useCardTypes();
  const cardTypeModalRef = useRef<CardTypeModalRef>(null);

  const { mutate, error } = useMutation<void, ErrorResponse, RegisterFormFields>(
    data => UserApi.edit(data),
    {
      onSuccess() {
        queryClient.invalidateQueries('user');
        onSuccess();
      },
    },
  );

  // Bezpieczne rozbicie daty
  const [birthYear, birthMonth, birthDay] = (user?.birthdate ?? '').split('-');

  const form = useForm<RegisterFormFields>({
    defaultValues: {
      birthDay: birthDay ? String(Number(birthDay)) : '',
      birthMonth: birthMonth ? String(Number(birthMonth)) : '',
      birthYear: birthYear ?? '',
      city: user?.city,
      country: user?.country as any,
      email: user?.email,
      firstName: user?.first_name,
      lastName: user?.last_name,
      legalFirstName: user?.legal_first_name ?? undefined,
      legalLastName: user?.legal_last_name ?? undefined,
      pesel: user?.pesel,
      phoneNumber: user?.phone,
      phonePrefix: user?.phone_prefix as any,
      postCode: user?.post_code,
      secondName: user?.second_name ?? undefined,
      street: user?.street,
      street_name: user?.street_name,
      building_number: user?.building_number,
      apartment_number: user?.apartment_number,
      voivodeship: user?.voivodeship as any,
    },
  });

  const { handleSubmit, setError, setValue } = form;

  const keyMapper = useCallback(
    (key: string) =>
      key === 'phone' ? 'phoneNumber' : (key as keyof RegisterFormFields),
    [],
  );
  const handleApiError = useApiErrorHandler(setError, keyMapper);

  // Czy wszystkie zgody wymagane są zaakceptowane
  const requiredAccepted = useMemo(() => {
    if (!user?.agreements) return false;
    const req = user.agreements.filter(a => Number(a.required) === 1);
    return req.length === 0 || req.every(a => Number(a.has_agree) === 1);
  }, [user?.agreements]);

  // Aktualna etykieta typu karty
  const currentCardTypeLabel = useMemo(() => {
    const ct = String(user?.card_type ?? '0');
    const arr = Array.isArray(cardTypeOptions) ? cardTypeOptions : [];
    return arr.find(o => String(o.value) === ct)?.label ?? 'Nie wybrano';
  }, [cardTypeOptions, user?.card_type]);

  // Czy można wyświetlić przycisk „Zmień typ”
  const canChangeType = useMemo(() => {
    const ct = Number(user?.card_type ?? 0);
    return requiredAccepted && (ct === 0 || ct === 1);
  }, [requiredAccepted, user?.card_type]);

  // Czy można zapisać formularz (twardy wymóg)
  const canSubmit = useMemo(() => {
    const ct = Number(user?.card_type ?? 0);
    return requiredAccepted && ct !== 0;
  }, [requiredAccepted, user?.card_type]);

  const fetchPhoto = useCallback(async () => {
    if (!user?.image) return;

    const imageUrl = new URL(user.image);
    const { data: response } = await HttpClient.getInstance().get(
      `${imageUrl.pathname.replace('/api', '')}${imageUrl.search}`,
      { responseType: 'blob' },
    );
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(new File([response], 'temp.png', { type: response.type || 'image/png' }));
    setValue('photo', dataTransfer.files);
    const photoInput = document.getElementById('photo');
    if (photoInput && photoInput instanceof HTMLInputElement) {
      photoInput.files = dataTransfer.files;
      photoInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }, [user?.image, setValue]);

  // Pobierz zdjęcie po zmianie usera
  useEffect(() => {
    fetchPhoto();
  }, [fetchPhoto]);

  const userLoaded = !!user;
  const agreementsReady = Array.isArray(user?.agreements);

// auto-open gdy: user wczytany, agreements dostępne, zgody wymagane zaakceptowane, card_type === 0
  useEffect(() => {
    if (!userLoaded || !agreementsReady) return;

    const ct = Number(user?.card_type ?? 0);
    const reqAccepted =
      (user?.agreements?.filter(a => Number(a.required) === 1) ?? []).every(
        a => Number(a.has_agree) === 1,
      );

    if (reqAccepted && ct === 0) {
      // drobny delay, żeby nie ścigać się z layoutem
      setTimeout(() => {
        cardTypeModalRef.current?.open();
      }, 50);
    }
  }, [userLoaded, agreementsReady, user?.agreements, user?.card_type]);

  useEffect(() => {
    handleApiError(error);
  }, [handleApiError, error]);

  const onSubmit: SubmitHandler<RegisterFormFields> = async data => {
    mutate(data);
  };

  return (
    <FormProvider {...form}>
      {/* Modal wyboru typu karty */}
      <CardTypeModal
        ref={cardTypeModalRef}
        options={cardTypeOptions}
        current={user?.card_type}
        requiredAgreementsAccepted={requiredAccepted}
      />

      <NewsletterModal child={user?.child === '1'} ref={newsletterModal} />

      <form noValidate className={styles.container} onSubmit={handleSubmit(onSubmit)}>
        {/* Pasek akcji na profilu: przycisk + aktualny wybór */}
        <div className={styles.actions_row}>
          {canChangeType && (
            <button
              type="button"
              className={styles.change_type}
              onClick={() => cardTypeModalRef.current?.open()}
              title="Zmień typ legitymacji"
            >
              <CreditCard size={16} style={{ marginRight: 8 }} />
              <Text id="change_card_type" defaultMessage="Zmień typ legitymacji" />
            </button>
          )}
          <div className={styles.currentTypeNote}>
            <small>
              <Text id="current_card_type" defaultMessage="Aktualny typ legitymacji" />:{' '}
              <strong>{currentCardTypeLabel}</strong>
            </small>
          </div>
        </div>

        <UserForm mode="edit" />

        <footer className={styles.footer}>
          <Button type="submit" disabled={!canSubmit}
                  title={!canSubmit ? 'Najpierw zaakceptuj wymagane zgody i wybierz typ legitymacji' : undefined}>
            <Text id="save" />
          </Button>
        </footer>
      </form>
    </FormProvider>
  );
};